/**
 * Custom SPZ Loader Plugin
 *
 * Replaces Babylon's built-in .spz handler to support both gzip and zstd
 * compressed SPZ files. Our custom SPZ generator uses a zstd envelope
 * instead of gzip — Babylon only recognizes gzip.
 *
 * Also decodes the SPZ v4 container natively. Babylon (through at least
 * 9.27.1) only reads v4 by fetching Adobe's WASM build from unpkg at load
 * time, which we don't want as a runtime dependency. v4 changes the
 * container, not the attribute encoding, so we unpack its streams into a
 * v3 buffer and reuse Babylon's ParseSpz.
 *
 * Also fixes an error-handling gap in Babylon's loader: if ParseSpz fails
 * or updateData throws, the promise is properly rejected instead of hanging
 * indefinitely.
 *
 * Decompression envelope detection:
 *   0x1F 0x8B        → gzip  (standard Niantic/Scaniverse SPZ)
 *   0x28 0xB5 0x2F 0xFD → zstd  (our custom generator)
 *   "NGSP" + version ≥ 4 → SPZ v4 container (per-attribute zstd streams)
 *   otherwise         → assume raw (already decompressed) SPZ
 */

import type { Scene } from "@babylonjs/core/scene";
import type {
    ISceneLoaderPluginAsync,
    ISceneLoaderAsyncResult,
    ISceneLoaderProgressEvent,
    ISceneLoaderPluginExtensions,
} from "@babylonjs/core/Loading/sceneLoader";
import { AssetContainer } from "@babylonjs/core/assetContainer";
import { GaussianSplattingMesh } from "@babylonjs/core/Meshes/GaussianSplatting/gaussianSplattingMesh";
import { GaussianSplattingMaterial } from "@babylonjs/core/Materials/GaussianSplatting/gaussianSplattingMaterial";
import { ParseSpz } from "@babylonjs/loaders/SPLAT/spz";
import { decompress as zstdDecompress } from "fzstd";
import { Logger } from "../../utils/Logger";

// Magic byte signatures
const GZIP_MAGIC_0 = 0x1f;
const GZIP_MAGIC_1 = 0x8b;
const ZSTD_MAGIC = 0xfd2fb528; // little-endian: 28 B5 2F FD
const SPZ_MAGIC = 0x5053474e; // "NGSP" — validated after decompression

// SPZ v4 container layout (nianticlabs/spz, NgspFileHeader):
//   0  magic u32 · 4 version u32 · 8 numPoints u32 · 12 shDegree u8
//   13 fractionalBits u8 · 14 flags u8 · 15 numStreams u8
//   16 tocByteOffset u32 · 20 reserved[12]
//   32..tocByteOffset          extension records (flags & 0x2): [type u32, byteLength u32, payload]
//   tocByteOffset..+N*16       TOC: N × [compressedSize u64, uncompressedSize u64]
//   then                       N independent zstd frames, in TOC order
// The writer omits empty streams, so a degree-0 file has no SH stream (N = 5).
const V4_HEADER_SIZE = 32;
const V4_FLAG_ANTIALIASED = 0x1;
const V4_FLAG_EXTENSIONS = 0x2;
const V4_EXT_COORDINATE_SYSTEM = 0xadbe0003;
/** CoordinateSystem values that mean "RUB": UNSPECIFIED (0) falls back to RUB (4). */
const V4_RUB_COORDINATE_SYSTEMS = new Set([0, 4]);
/** Highest SH degree the format defines (v4 added degree 4). */
const SPZ_MAX_SH_DEGREE = 4;
/**
 * Upstream's sanity bound on numPoints versus file size: the positions stream alone
 * is 9 bytes/point and no real stream compresses better than 1024:1. Mirrors
 * kMaxCompressionRatio / kMinBytesPerPoint in nianticlabs/spz src/cc/load-spz.cc.
 */
const V4_MAX_COMPRESSION_RATIO = 1024;
const V4_MIN_BYTES_PER_POINT = 9;
/** v3 header bytes: magic, version, numPoints, shDegree, fractionalBits, flags, reserved. */
const V3_HEADER_SIZE = 16;

/**
 * Detect the compression envelope of an SPZ file.
 */
function detectEnvelope(data: ArrayBuffer): "gzip" | "zstd" | "v4" | "raw" {
    const u8 = new Uint8Array(data, 0, Math.min(4, data.byteLength));
    if (u8[0] === GZIP_MAGIC_0 && u8[1] === GZIP_MAGIC_1) {
        return "gzip";
    }
    if (data.byteLength >= 4) {
        const view = new DataView(data);
        // Zstd magic: 0x28 0xB5 0x2F 0xFD (little-endian u32 = 0xFD2FB528)
        if (view.getUint32(0, true) === ZSTD_MAGIC) {
            return "zstd";
        }
        // Raw v2/v3 also starts with "NGSP"; the version field tells them apart.
        if (data.byteLength >= 8 && view.getUint32(0, true) === SPZ_MAGIC && view.getUint32(4, true) >= 4) {
            return "v4";
        }
    }
    return "raw";
}

/**
 * Warn about extension records we don't honor. Only the coordinate-system
 * record changes how the data should be read; everything else is metadata.
 */
function inspectV4Extensions(data: ArrayBuffer, start: number, end: number): void {
    const view = new DataView(data);
    let offset = start;
    while (offset + 8 <= end) {
        const type = view.getUint32(offset, true);
        const byteLength = view.getUint32(offset + 4, true);
        if (type === V4_EXT_COORDINATE_SYSTEM && byteLength >= 4 && offset + 12 <= end) {
            const coordinateSystem = view.getUint32(offset + 8, true);
            if (!V4_RUB_COORDINATE_SYSTEMS.has(coordinateSystem)) {
                Logger.warn(
                    `[SPZLoaderPlugin] SPZ declares coordinate system ${coordinateSystem} via extension; ` +
                        "it is not applied, the data is treated as RUB and may appear misoriented"
                );
            }
        }
        offset += 8 + byteLength;
    }
}

/**
 * Unpack an SPZ v4 container into the equivalent v3 byte layout that
 * Babylon's ParseSpz reads. The per-attribute encodings are identical
 * between v3 and v4, and v3 concatenates the attributes in the same order
 * v4 stores its streams: positions, alphas, colors, scales, rotations, SH.
 */
function decodeSpzV4(data: ArrayBuffer): ArrayBuffer {
    const view = new DataView(data);
    const version = view.getUint32(4, true);
    if (version !== 4) {
        throw new Error(`Unsupported SPZ version: ${version} (expected 2, 3 or 4)`);
    }
    if (data.byteLength < V4_HEADER_SIZE) {
        throw new Error(`SPZ v4 data too small: ${data.byteLength} bytes (minimum ${V4_HEADER_SIZE})`);
    }
    const numPoints = view.getUint32(8, true);
    const shDegree = view.getUint8(12);
    const fractionalBits = view.getUint8(13);
    const flags = view.getUint8(14);
    const numStreams = view.getUint8(15);
    const tocByteOffset = view.getUint32(16, true);

    if (shDegree > SPZ_MAX_SH_DEGREE) {
        throw new Error(`Unsupported SPZ SH degree: ${shDegree} (maximum ${SPZ_MAX_SH_DEGREE})`);
    }
    if (numPoints === 0 || numPoints > (data.byteLength * V4_MAX_COMPRESSION_RATIO) / V4_MIN_BYTES_PER_POINT) {
        throw new Error(`Invalid SPZ v4 point count: ${numPoints} for a ${data.byteLength}-byte file`);
    }

    const shCoeffs = (shDegree + 1) * (shDegree + 1) - 1;
    const expectedSizes = [
        numPoints * 9, // positions: 3 × 24-bit fixed point
        numPoints, // alphas
        numPoints * 3, // colors
        numPoints * 3, // scales
        numPoints * 4, // rotations: smallest-three, 4 bytes
        numPoints * shCoeffs * 3, // spherical harmonics
    ].filter((size) => size > 0);
    if (numStreams !== expectedSizes.length) {
        throw new Error(`Unsupported SPZ v4 stream count: ${numStreams} (expected ${expectedSizes.length})`);
    }
    const tocEnd = tocByteOffset + numStreams * 16;
    if (tocByteOffset < V4_HEADER_SIZE || tocEnd > data.byteLength) {
        throw new Error(`Invalid SPZ v4 table of contents offset: ${tocByteOffset}`);
    }
    if (flags & V4_FLAG_EXTENSIONS) {
        inspectV4Extensions(data, V4_HEADER_SIZE, tocByteOffset);
    }

    // Validate the whole TOC before allocating the output buffer.
    const compressedSizes: number[] = [];
    let srcEnd = tocEnd;
    for (let i = 0; i < numStreams; i++) {
        const compressedSize = Number(view.getBigUint64(tocByteOffset + i * 16, true));
        const uncompressedSize = Number(view.getBigUint64(tocByteOffset + i * 16 + 8, true));
        if (uncompressedSize !== expectedSizes[i]) {
            throw new Error(
                `SPZ v4 stream ${i} has ${uncompressedSize} bytes, expected ${expectedSizes[i]} for ${numPoints} points`
            );
        }
        srcEnd += compressedSize;
        if (srcEnd > data.byteLength) {
            throw new Error(`SPZ v4 stream ${i} is truncated`);
        }
        compressedSizes.push(compressedSize);
    }

    const totalSize = expectedSizes.reduce((sum, size) => sum + size, V3_HEADER_SIZE);
    const out = new Uint8Array(totalSize);
    const outView = new DataView(out.buffer);
    outView.setUint32(0, SPZ_MAGIC, true);
    outView.setUint32(4, 3, true);
    outView.setUint32(8, numPoints, true);
    out[12] = shDegree;
    out[13] = fractionalBits;
    // ParseSpz reads any nonzero flags byte as "trained with antialiasing",
    // so pass only that bit through.
    out[14] = flags & V4_FLAG_ANTIALIASED;
    out[15] = 0;

    let srcOffset = tocEnd;
    let dstOffset = V3_HEADER_SIZE;
    for (let i = 0; i < numStreams; i++) {
        // Let fzstd allocate, then copy. Passing `out.subarray(...)` as fzstd's
        // output buffer measured ~4x slower (1.2 s vs 0.3 s on a 1.1M-splat file).
        const result = zstdDecompress(new Uint8Array(data, srcOffset, compressedSizes[i]));
        if (result.byteLength !== expectedSizes[i]) {
            throw new Error(
                `SPZ v4 stream ${i} decompressed to ${result.byteLength} bytes, expected ${expectedSizes[i]}`
            );
        }
        out.set(result, dstOffset);
        srcOffset += compressedSizes[i];
        dstOffset += expectedSizes[i];
    }
    return out.buffer;
}

/**
 * Decompress gzip data using the native DecompressionStream API.
 */
async function decompressGzip(data: ArrayBuffer): Promise<ArrayBuffer> {
    const stream = new ReadableStream({
        start(controller) {
            controller.enqueue(new Uint8Array(data));
            controller.close();
        },
    });
    const decompressed = stream.pipeThrough(new DecompressionStream("gzip"));
    return new Response(decompressed).arrayBuffer();
}

/**
 * Decompress a potentially compressed SPZ buffer.
 * Returns the raw SPZ ArrayBuffer ready for ParseSpz.
 */
async function decompressSPZ(data: ArrayBuffer): Promise<ArrayBuffer> {
    const envelope = detectEnvelope(data);

    switch (envelope) {
        case "gzip":
            return decompressGzip(data);
        case "v4":
            return decodeSpzV4(data);
        case "zstd": {
            const result = zstdDecompress(new Uint8Array(data));
            // fzstd may return a view into a larger buffer — copy to an exact-size ArrayBuffer
            return new Uint8Array(result).buffer;
        }
        case "raw":
            return data;
    }
}

/**
 * Validate that the decompressed data has a valid SPZ header.
 */
function validateSPZHeader(data: ArrayBuffer): void {
    if (data.byteLength < 16) {
        throw new Error(`SPZ data too small: ${data.byteLength} bytes (minimum 16)`);
    }
    const magic = new DataView(data).getUint32(0, true);
    if (magic !== SPZ_MAGIC) {
        throw new Error(
            `Invalid SPZ magic: 0x${magic.toString(16).padStart(8, "0")} (expected 0x${SPZ_MAGIC.toString(16).padStart(8, "0")})`
        );
    }
    const version = new DataView(data).getUint32(4, true);
    if (version !== 2 && version !== 3) {
        throw new Error(`Unsupported SPZ version: ${version} (expected 2 or 3)`);
    }
}

export class SPZLoaderPlugin implements ISceneLoaderPluginAsync {
    readonly name = "spz";
    readonly extensions: ISceneLoaderPluginExtensions = {
        ".spz": { isBinary: true },
    };

    async importMeshAsync(
        _meshesNames: string | readonly string[] | null | undefined,
        scene: Scene,
        data: unknown,
        _rootUrl: string,
        _onProgress?: (event: ISceneLoaderProgressEvent) => void,
        _fileName?: string,
    ): Promise<ISceneLoaderAsyncResult> {
        const raw = data as ArrayBuffer;

        // 1. Decompress (gzip, zstd, or raw pass-through)
        const decompressed = await decompressSPZ(raw);

        // 2. Validate SPZ header
        validateSPZHeader(decompressed);

        // 3. Parse SPZ into GaussianSplatting buffer format
        const parsedSPZ = await ParseSpz(decompressed, scene, {});
        if (!parsedSPZ.data || !parsedSPZ.data.byteLength) {
            throw new Error("ParseSpz returned empty data");
        }

        // 4. Create GaussianSplattingMesh and populate it
        const gaussianSplatting = new GaussianSplattingMesh("GaussianSplatting", null, scene);

        if (parsedSPZ.trainedWithAntialiasing) {
            const gsMaterial = gaussianSplatting.material as GaussianSplattingMaterial;
            gsMaterial.kernelSize = 0.1;
            gsMaterial.compensation = true;
        }

        // Pass shDegree explicitly: without it Babylon uploads the SH textures
        // but renders at degree 0, dropping view-dependent color.
        gaussianSplatting.updateData(parsedSPZ.data, parsedSPZ.sh, { flipY: false }, undefined, parsedSPZ.shDegree);

        return {
            meshes: [gaussianSplatting],
            particleSystems: [],
            skeletons: [],
            animationGroups: [],
            transformNodes: [],
            geometries: [],
            lights: [],
            spriteManagers: [],
        };
    }

    async loadAsync(
        scene: Scene,
        data: unknown,
        rootUrl: string,
        onProgress?: (event: ISceneLoaderProgressEvent) => void,
        fileName?: string,
    ): Promise<void> {
        await this.importMeshAsync(null, scene, data, rootUrl, onProgress, fileName);
    }

    async loadAssetContainerAsync(
        scene: Scene,
        data: unknown,
        rootUrl: string,
        onProgress?: (event: ISceneLoaderProgressEvent) => void,
        fileName?: string,
    ): Promise<AssetContainer> {
        const container = new AssetContainer(scene);
        scene._blockEntityCollection = true;
        try {
            const result = await this.importMeshAsync(null, scene, data, rootUrl, onProgress, fileName);
            for (const mesh of result.meshes) {
                container.meshes.push(mesh);
            }
        } finally {
            scene._blockEntityCollection = false;
        }
        return container;
    }
}
